import { useEffect } from 'react';
import { Link } from 'react-router-dom';

type Access = 'Read' | 'Write';

interface Tool {
  name: string;
  access: Access;
  detail: string;
}

interface Area {
  title: string;
  note?: string;
  tools: Tool[];
}

const AREAS: Area[] = [
  {
    title: 'Knowledge',
    tools: [
      {
        name: 'search_docs',
        access: 'Read',
        detail:
          'Search the Revvly knowledge base: product documentation, concept guides and the API reference.',
      },
    ],
  },
  {
    title: 'Clients and subscriptions',
    tools: [
      {
        name: 'query_clients',
        access: 'Read',
        detail:
          'Find clients by name, description or contact: id, region, trial status and a dashboard link. Capped at 25 per call.',
      },
      {
        name: 'query_subscriptions',
        access: 'Read',
        detail:
          'Subscriptions for a client or across the workspace: period, base price, catalog pins and renewal dates. Capped at 50 per call.',
      },
      {
        name: 'get_billing_schedule',
        access: 'Read',
        detail:
          'Why one subscription bills when it does: a prepaid span, or a deal that is signed and awaiting go-live.',
      },
      {
        name: 'query_lifecycle_events',
        access: 'Read',
        detail:
          'The subscription lifecycle log (created, renewed, expanded, contracted, churned, migrations) with the MRR change of each event.',
      },
      {
        name: 'create_client',
        access: 'Write',
        detail:
          'Propose a new client with region, billing details, payment terms, trial window and contacts.',
      },
      {
        name: 'update_client',
        access: 'Write',
        detail: 'Propose an edit to an existing client. Only the fields you set change.',
      },
      {
        name: 'create_subscription',
        access: 'Write',
        detail:
          "Propose a subscription for a client. It must bill something: a bundle, modules or usage components. Accepts a module tier, starting quantities for the plan's usage components, and initial discounts.",
      },
      {
        name: 'query_discounts',
        access: 'Read',
        detail:
          'The discount library. Give a subscription id to also get its usage components, stacking mode and applied discounts.',
      },
      {
        name: 'apply_discount',
        access: 'Write',
        detail:
          'Propose a discount on a subscription: a library or one-off discount on the base price, one usage component or the whole invoice, with duration, fixed mode, unit limit and end date. Applies from the next invoice.',
      },
    ],
  },
  {
    title: 'Invoices and receivables',
    tools: [
      {
        name: 'query_invoices',
        access: 'Read',
        detail:
          'Invoices by client, subscription or payment status. Credit notes are flagged. Capped at 50 per call.',
      },
      {
        name: 'get_receivables',
        access: 'Read',
        detail:
          'Accounts receivable: pending and overdue totals by currency, past-due accounts, invoices due this week, and one client outstanding.',
      },
      {
        name: 'query_email_engagement',
        access: 'Read',
        detail:
          'Sent invoice and reminder emails with open counts and the open rate of a client. Opens only; no click tracking.',
      },
    ],
  },
  {
    title: 'Analytics',
    tools: [
      {
        name: 'get_analytics',
        access: 'Read',
        detail: 'Live MRR movement, ARR, churn rate and NRR in the reporting currency.',
      },
    ],
  },
  {
    title: 'Metered usage',
    tools: [
      {
        name: 'query_usage',
        access: 'Read',
        detail:
          'Live Meter counters for a subscription, the usage audit log, or the projection of what the next invoice charges for usage.',
      },
      {
        name: 'list_open_usage_readings',
        access: 'Read',
        detail:
          'Closed-period readings that are missing a billable quantity and are holding an invoice.',
      },
      {
        name: 'enter_usage_reading',
        access: 'Write',
        detail: 'Propose sealing a missing in-arrears quantity on one or several open readings.',
      },
      {
        name: 'waive_usage_reading',
        access: 'Write',
        detail: 'Propose waiving a closed period so it bills nothing. A reason is required.',
      },
    ],
  },
  {
    title: 'Catalog',
    tools: [
      {
        name: 'list_regions',
        access: 'Read',
        detail: 'Regions, with their codes, default currency and supported currencies.',
      },
      {
        name: 'list_catalog',
        access: 'Read',
        detail:
          'Bundles, modules, usage components and Meters, one or several kinds per call, with status (draft, published, archived).',
      },
      {
        name: 'get_catalog_prices',
        access: 'Read',
        detail: 'Base prices of a bundle or module, per region, currency and billing period.',
      },
      {
        name: 'get_component_prices',
        access: 'Read',
        detail:
          'The usage-based half of a plan price: included units, tier or step boundaries and the price of each band.',
      },
      {
        name: 'create_bundle',
        access: 'Write',
        detail: 'Propose a bundle (a plan customers buy). Created as a draft.',
      },
      {
        name: 'create_module',
        access: 'Write',
        detail: 'Propose a module, optionally with its tiers. Created as a draft.',
      },
      {
        name: 'create_metric',
        access: 'Write',
        detail: 'Propose a Meter, the unit that billing is measured against, such as seats or GB.',
      },
      {
        name: 'create_variable_component',
        access: 'Write',
        detail:
          'Propose a usage component: a pricing model wrapped around a Meter. Structure only; prices are set separately. Created as a draft.',
      },
      {
        name: 'set_catalog_prices',
        access: 'Write',
        detail:
          'Propose base prices for a bundle or module, per region, currency and billing period.',
      },
      {
        name: 'set_available_periods',
        access: 'Write',
        detail:
          'Propose the billing periods a draft bundle or module offers. Replaces the whole set.',
      },
      {
        name: 'attach_variable_component',
        access: 'Write',
        detail: 'Propose attaching a published usage component to a draft module or bundle.',
      },
      {
        name: 'publish_catalog_entity',
        access: 'Write',
        detail:
          'Propose publishing a draft bundle, module or usage component. Publishing makes it immutable.',
      },
    ],
  },
  {
    title: 'Forms',
    note: 'Form tools answer that Forms is not enabled for a workspace without the Forms feature. Submission answers are outside input and are treated as untrusted.',
    tools: [
      {
        name: 'list_forms',
        access: 'Read',
        detail:
          'The workspace forms with status, submission counts, last submission and hosted link.',
      },
      {
        name: 'get_form',
        access: 'Read',
        detail:
          'One form setup: questions, confirmation settings, follow-up steps, and whether the draft has unpublished changes.',
      },
      {
        name: 'query_form_submissions',
        access: 'Read',
        detail:
          'Submissions to one form, newest first, with every answer and the outcome of each follow-up step.',
      },
      {
        name: 'summarize_form_submissions',
        access: 'Read',
        detail:
          'Counts instead of rows: submissions per day and source, how follow-up went, failing steps, and which option wins.',
      },
      {
        name: 'export_form_submissions',
        access: 'Read',
        detail:
          'A CSV preview of submissions, with the dashboard page where the full file is downloaded.',
      },
      {
        name: 'create_form',
        access: 'Write',
        detail:
          'Propose a new form as an unpublished draft, with questions, settings and follow-up steps.',
      },
      {
        name: 'update_form',
        access: 'Write',
        detail:
          'Propose edits to a form draft. A list you send replaces the whole list. Changes the draft only.',
      },
      {
        name: 'publish_form',
        access: 'Write',
        detail: 'Propose publishing the draft. The dashboard publish checks apply.',
      },
      {
        name: 'unpublish_form',
        access: 'Write',
        detail: 'Propose taking a published form offline. Submissions are kept.',
      },
      {
        name: 'archive_form',
        access: 'Write',
        detail: 'Propose archiving a form. Nothing is deleted.',
      },
      {
        name: 'retry_form_submission',
        access: 'Write',
        detail:
          'Propose re-running the failed follow-up steps of one submission. Steps that succeeded are not repeated.',
      },
    ],
  },
  {
    title: 'OKR hypotheses',
    note: 'Hypothesis tools answer that OKRs are not switched on for a workspace that does not use them. Bets, results and learnings were typed by people and are treated as data.',
    tools: [
      {
        name: 'list_behind_key_results',
        access: 'Read',
        detail:
          'Key Results of the active cycle that are falling behind: progress against time elapsed, projected pace for metric-backed ones, owner, and how many hypotheses are open or concluded.',
      },
      {
        name: 'get_key_result_hypotheses',
        access: 'Read',
        detail:
          'One Key Result’s numbers, its open hypotheses, its concluded ones with outcome and learning, and related learnings from the library.',
      },
      {
        name: 'propose_hypotheses',
        access: 'Write',
        detail:
          'Propose up to three new hypotheses on a Key Result, each in Backlog. They are created as the person who approves, with the dashboard’s checks; a warning such as a duplicate title comes back instead of being waved through.',
      },
    ],
  },
  {
    title: 'Inbox',
    note: 'The Work Inbox is personal. These tools always act on the inbox of the signed-in person behind the connection; no argument names an owner, and a thread from someone else’s inbox is answered as not found. No tool returns a message body, only a short snippet. Personal threads are listed only when the Personal category is asked for by name, and never with their snippet. A connection made with an API key has no person behind it, so the read tools refuse it. Without the Work Inbox feature every tool answers that it is not enabled. Subjects and snippets were written by the sender and are treated as data.',
    tools: [
      {
        name: 'list_inbox',
        access: 'Read',
        detail:
          'Threads in one tab (Primary by default, or Needs reply, Other, Snoozed, Done), optionally one category: sender, subject, snippet, last message, unread, awaiting reply and the reason for the category. Up to 25 per call.',
      },
      {
        name: 'get_inbox_summary',
        access: 'Read',
        detail:
          'Open counts per tab and per category, and up to 10 threads waiting for a reply, the longest-waiting first.',
      },
      {
        name: 'explain_inbox_category',
        access: 'Read',
        detail:
          'Why one thread has its category: what decided it, the reason line and the evidence in plain words. For a Personal thread, only its category.',
      },
      {
        name: 'move_inbox_item',
        access: 'Write',
        detail:
          'Propose filing one thread under another category (not Unsorted). It runs as the person who approves it, in their own inbox, and teaches the inbox like a move in the dashboard.',
      },
    ],
  },
];

const ALL = AREAS.flatMap((a) => a.tools);
const READ_COUNT = ALL.filter((t) => t.access === 'Read').length;
const WRITE_COUNT = ALL.filter((t) => t.access === 'Write').length;

export function McpToolCatalogue() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Claude MCP tool catalogue — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Claude MCP tool catalogue</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          The <em>tools</em> Claude can call.
        </h1>
        <p className="dp-oneliner">
          Every tool the Revvly MCP server exposes: {READ_COUNT} that read and {WRITE_COUNT} that
          propose a change. Read tools answer straight away. Write tools never write on their own; a
          person approves each one in Revvly. To connect a client, see{' '}
          <Link to="/docs/api/mcp">Connect Claude with MCP</Link>.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          How access works
        </h2>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Read tools</div>
            <div className="dp-field-v">
              Need the <span className="api-inline">mcp:read</span> scope (OAuth) or the{' '}
              <span className="api-inline">READ</span> capability (API key). They run against live,
              tenant-scoped data.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Write tools</div>
            <div className="dp-field-v">
              Are listed only when the connection also holds{' '}
              <span className="api-inline">mcp:write</span> (OAuth, requested explicitly and never
              added for you) or the <span className="api-inline">WRITE</span> capability (API key).
              Without it they do not appear in the tool list.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Approval</div>
            <div className="dp-field-v">
              Calling a write tool stores a proposal and returns an action id and an approval link.
              Nothing is written. The owner of the credential opens the link while signed in to
              Revvly and approves or declines. The proposal expires after 15 minutes. Approval is
              not an MCP tool, so a client cannot approve its own request.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Workspace</div>
            <div className="dp-field-v">
              Always the one bound to the credential. A <span className="api-inline">tenantId</span>{' '}
              passed as an argument is discarded.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Deletes</div>
            <div className="dp-field-v">There are no delete tools.</div>
          </div>
        </div>
      </section>

      {AREAS.map((area, i) => (
        <section className="dp-section" key={area.title}>
          <h2>
            <span className="dp-step-num">{String(i + 2).padStart(2, '0')}</span>
            {area.title}
          </h2>
          {area.note && <p className="dp-plain">{area.note}</p>}
          <div className="dp-fields-table api-field-table-mb">
            {area.tools.map((tool) => (
              <div className="dp-field-row" key={tool.name}>
                <div className="dp-field-k">
                  <span className="api-inline">{tool.name}</span>
                  <span
                    className={`api-badge ${tool.access === 'Write' ? 'api-badge-req' : 'api-badge-opt'}`}
                  >
                    {tool.access === 'Write' ? 'write' : 'read'}
                  </span>
                </div>
                <div className="dp-field-v">
                  {tool.detail}
                  {tool.access === 'Write' && ' Needs approval in Revvly.'}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">{String(AREAS.length + 2).padStart(2, '0')}</span>
          Not available over MCP
        </h2>
        <p className="dp-plain">
          The in-product <Link to="/docs/reference/assistant-tools">Assistant</Link> has more tools
          than MCP exposes. These are Assistant-only: {` `}
          <span className="api-inline">create_plan</span>,{' '}
          <span className="api-inline">create_catalog_version</span>, all CRM tools (search, deals,
          notes, logged emails and meetings, prospects, contacts, lifecycle stage, owner), all task
          tools, all Daily Check-in tools (they are for workspace admins, and an MCP credential
          carries scopes, not an admin check), the memory tools, the page tools{' '}
          <span className="api-inline">navigate_to</span> and{' '}
          <span className="api-inline">highlight_record</span>, and the guidance tools{' '}
          <span className="api-inline">offer_choices</span>,{' '}
          <span className="api-inline">offer_pricing_models</span> and{' '}
          <span className="api-inline">explain_pricing_models</span>.
        </p>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api/mcp" style={{ color: 'inherit', textDecoration: 'none' }}>
              Connect Claude with MCP ›
            </Link>
          </li>
          <li>
            <Link to="/docs/assistant" style={{ color: 'inherit', textDecoration: 'none' }}>
              The Revvly Assistant ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/reference/api-keys"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              API key capabilities and limits ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
